import * as React from "react";
import {cx}       from "emotion";

import "./searchable-multi-select.scss";

import {bindInput}      from "../../react/bind-input";
import {cs}             from "../../react/chain-services";
import {keyed}          from "../../react/keyed";
import {OnMounted}      from "../../react/on-mounted";
import {Static2}        from "../../react/static-2";
import {UseState}       from "../../react/use-state";
import {Responsive2}    from "@Common/ui-components/responsive-2";
import {chain}          from "../../utils/fs";
import {isIe}           from "../debug/is-ie-edge-or-firefox";
import {Dropdown}       from "../dropdown/dropdown";
import {Checkbox}       from "../heavy/common/design2/checkbox/checkbox";

import {KeyboardSearchController} from "./keyboard-search-controller";

export const SearchableMultiSelect = <Item,>(
    {
        list, placeholder, disabled=false, displayList=true, searchLabel="Search",
        className=undefined,
        getLabel=v=>String(v),
        selectAll=true,
        getSublabel=undefined, onSelect, onSelectAll, onRemove, isSelected, xdQaName=undefined, style=undefined,
        getXdQaName=undefined,
        captureOutsideClicks=true, // Use this if select is inside modal, if outside modal, don't use, it will be more natural
        loading=undefined,
        scrollCloses=true,
        mapSearchAsItemFilter=undefined,
        ...props
    }:{
        captureOutsideClicks  ?: boolean,
        className             ?: string,
        disabled              ?: boolean,
        displayList           ?: boolean,
        getLabel              ?: (item:Item) => string,
        getSublabel           ?: (item:Item) => JSX.Element,
        isSelected             : (item:Item) => boolean,
        list                   : Item[],
        loading               ?: boolean,
        mapSearchAsItemFilter ?: (search:string) => ((item:Item) => boolean),
        onSelect               : (item:Item) => void,
        selectAll             ?: boolean,
        onSelectAll           ?: () => void
        onRemove               : (item:Item) => void,
        placeholder            : string,
        scrollCloses          ?: boolean,
        searchLabel           ?: string,
        style                 ?: {color?: string, size?: string},
        xdQaName              ?: string,
        getXdQaName           ?: (item:Item) => string,
        // [prop:string]          : any,
    }
) => cs(
    ["selectedItems", (_, next) => next(list?.filter(isSelected) ?? [])],
    ["expanded", ({}, next) => UseState({initValue: false, next})],
    ["responsive", ({}, next) => Responsive2({
        translate: (w) => w < 1024 ? "mobile" : "desktop",
        next,
    })],
    ({responsive, selectedItems}) => Dropdown({
        forcedExpandBottom: true,
        forcedExpandLeft: true,
        mobilePinToTop: responsive === "mobile",
        scrollCloses: scrollCloses,
        renderToggle: ({domRef, showExpand}) => (
            <div
                className={cx("searchable-multi-select searchable-multi-select-2s8 toggle", {active: selectedItems.length !== 0}, style?.size, style?.color, className)}
                {...props}
                ref={domRef}
            >
                <div className="list-container">
                    <div className="text" xd-qa-type="dropdown" xd-qa-name={xdQaName} onClick={() => !disabled && showExpand()}>
                        {selectedItems.length === 0 || !displayList ? (
                            placeholder
                        ) : (
                            <div className={cx("items", {active: false})}>
                                {selectedItems.map((item, i) => cs(
                                    keyed(i),
                                    () => (
                                        <div className="item" xd-qa-type="badge" xd-qa-name={getXdQaName?.(item)}>
                                            <div className="text" xd-qa-type="label" xd-qa-name="Selected">
                                                {getLabel(item)}
                                            </div>

                                            {/*<i className="fa fa-close"/>*/}
                                            <div
                                                className="close"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onRemove(item);
                                                }}
                                                xd-qa-type="icon"
                                                xd-qa-name="Remove"
                                            >&times;</div>
                                        </div>
                                    )
                                ))}
                            </div>
                        )}
                    <i className="fa fa-angle-down"/>
                    </div>
                </div>
            </div>
        ),
        renderExpand: ({close, width, position, domRef}) => cs(
            ["ksc", (_, next) => KeyboardSearchController({
                getList: (searching) => list && searching ? (
                    list.filter(mapSearchAsItemFilter ? mapSearchAsItemFilter(searching) : (item) => (
                        getLabel(item).toLowerCase().includes(searching.toLowerCase()) ||
                        getSublabel?.(item)?.toString().toLowerCase().includes(searching.toLowerCase())
                    ))
                ) : (
                    list
                ),
                onSelect: (item) => {
                    onSelect(item);
                },
                onClose: close,
                renderListItem: (item, {index, focused, ref}) => {
                    return (
                        <div
                            key={index}
                            className={cx("item", {
                                focused,
                                "has-sub-label": !!getSublabel?.(item),
                            })}
                            xd-qa-type="dropdown_item"
                            xd-qa-name={getXdQaName?.(item)}
                            ref={ref}
                            onClick={() => !isSelected(item) ? onSelect(item) : onRemove(item)}
                        >
                            <div
                                className="label"
                                xd-qa-type="dropdown_item"
                                xd-qa-name="Item"
                            >
                                <div
                                    className={cx("item")}
                                    key={0}
                                >
                                    {Checkbox({
                                        checked: isSelected(item),
                                        xdQaName: getXdQaName?.(item) + " Checkbox"
                                    })}
                                    <span
                                        className="label"
                                        xd-qa-type="dropdown_item"
                                        xd-qa-name="Name"
                                    >
                                        {getLabel(item)}
                                    </span>
                                    {getSublabel?.(item) &&
                                    <div
                                        className="sub-label"
                                        xd-qa-type="dropdown_item"
                                        xd-qa-name="Sub Label"
                                    >
                                        {getSublabel?.(item)}
                                    </div>
                                    }
                                </div>
                            </div>
                        </div>
                    );
                },
                next,
            })],
            ["searchRef", (_, next) => Static2({next})],
            ({ksc, searchRef}) => (

                <div
                    className={cx("searchable-multi-select-2s8 expand", {capture: captureOutsideClicks}, className)}
                    onClick={close}
                    ref={domRef}
                >

                    <div
                        className={cx("box", {ie: isIe(), mobile: responsive === "mobile"})}
                        style={{
                            ...position,
                            top: position.top,
                            width,
                            maxHeight: window.innerHeight - position.top - 5,
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                        onWheel={(e) => {
                            e.stopPropagation();
                        }}
                    >
                        {displayList ?
                            <div className="list-container">
                                <div className="text" xd-qa-type="dropdown" xd-qa-name={xdQaName + " Selected Area"}>
                                    <div className={cx("items", {active: true})}>

                                        {selectedItems.map((item, i) => cs(
                                            keyed(i),
                                            () => (
                                                <div className="item">
                                                    <div className="text" xd-qa-type="label" xd-qa-name={getLabel(item)}>
                                                        {getLabel(item)}
                                                    </div>
                                                    <div
                                                        className="close"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onRemove(item);
                                                        }}
                                                        xd-qa-type="icon"
                                                        xd-qa-name="Remove"
                                                    >&times;</div>
                                                </div>
                                            )
                                        ))}
                                    </div>
                                </div>
                            </div> : <></>
                        }
                        <div className="search">
                            <i className="fa fa-search"/>
                            <input {...{
                                ...bindInput(ksc.searching),
                                placeholder: searchLabel,
                                ref: searchRef.set,
                                onKeyDown: (e) => {
                                    if (ksc.keyDown(e)) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        return;
                                    }
                                },
                                "xd-qa-type":"textbox",
                                "xd-qa-name":"Search " + xdQaName,
                            }}/>
                            {OnMounted({
                                action: () => searchRef.get().focus(),
                            })}
                            {responsive === "mobile" && <div onClick={close} className="close" xd-qa-type="icon" xd-qa-name="Expand Close">&times;</div>}
                        </div>
                        <div className="list" ref={ksc.listRef} onScroll={ksc.checkAddScroll}>
                            {chain(
                                ksc.renderList(),
                                (_) => _?.length ? _ : (
                                    <div className="no-record">
                                        No record(s)
                                    </div>
                                )
                            )}
                        </div>
                        {loading && (
                            <div className="loading">
                                Loading...
                            </div>
                        )}
                        {!loading && selectAll && (
                            <div
                            className="select-all"
                            onClick={() => onSelectAll()}>
                                {Checkbox({
                                checked: selectedItems.length === list?.length ? true : selectedItems.length === 0 ? false : "midway",
                                xdQaName: "Select All",
                                })}
                                {selectedItems.length === 0 ?
                                    <span className="text" xd-qa-type="label" xd-qa-name="Select All">Select All ({selectedItems.length})</span> :
                                    <span className="text" xd-qa-type="label" xd-qa-name="De-Select All">De-Select All ({selectedItems.length})</span>}
                            </div>
                        )}
                    </div>
                </div>
            )
        ),
    })
);
